import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { PlanoAcao } from '@/types/plano-acao';
import { usePlanoAcao } from '@/hooks/usePlanoAcao';
import { useCategorias } from '@/hooks/useCategorias';
import { dateTimeLocalToISO, formatDateTimeLocalBrazil } from '@/utils/timezone';
import { supabase } from '@/integrations/supabase/client';

interface PlanoAcaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  campoRespostaId?: string;
  respostaId: string;
  campoId: string;
  tituloSugestao?: string;
  justificativaSugestao?: string;
  planoExistente?: PlanoAcao;
  onSuccess?: () => void;
}

interface UsuarioGrupo {
  id: string;
  name: string;
}

export function PlanoAcaoModal({
  isOpen,
  onClose,
  campoRespostaId,
  respostaId,
  campoId,
  tituloSugestao,
  justificativaSugestao,
  planoExistente,
  onSuccess,
}: PlanoAcaoModalProps) {
  const form = useForm<Partial<PlanoAcao>>({
    defaultValues: planoExistente || {
      titulo: tituloSugestao || '',
      justificativa: justificativaSugestao || '',
      status: 'aberto',
      prioridade: 'media',
    },
  });
  
  const { categorias } = useCategorias();
  const categoriasPlanoAcao = categorias.filter(c => c.tipo === 'plano_acao');
  const { createPlanoAcao, updatePlanoAcao, isCreating, isUpdating } = usePlanoAcao();
  const [usuariosGrupo, setUsuariosGrupo] = React.useState<UsuarioGrupo[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = React.useState(false);
  
  const categoriaIdAtual = form.watch('categoria_id');

  // Buscar usuários do grupo da categoria selecionada
  React.useEffect(() => {
    async function fetchUsuariosDoGrupo() {
      if (!categoriaIdAtual) {
        setUsuariosGrupo([]);
        return;
      }
      
      setLoadingUsuarios(true);
      try {
        const { data: cat } = await supabase
          .from('categorias_servico')
          .select('grupo_id')
          .eq('id', categoriaIdAtual)
          .single();
        
        if (!cat?.grupo_id) {
          setUsuariosGrupo([]);
          setLoadingUsuarios(false);
          return;
        }
        
        const { data: userGroups } = await supabase
          .from('user_groups')
          .select('user_id')
          .eq('group_id', cat.grupo_id);
        
        if (!userGroups?.length) {
          setUsuariosGrupo([]);
          setLoadingUsuarios(false);
          return;
        }
        
        const userIds = userGroups.map(ug => ug.user_id);
        
        const { data: users } = await supabase
          .from('users')
          .select('id, name')
          .in('id', userIds)
          .order('name');
        
        setUsuariosGrupo(users || []);
      } catch (err) {
        console.error('Erro ao buscar usuários do grupo:', err);
        setUsuariosGrupo([]);
      } finally {
        setLoadingUsuarios(false);
      }
    }
    
    fetchUsuariosDoGrupo();
  }, [categoriaIdAtual]);
  
  // Resetar o form quando o modal abre com novos dados
  React.useEffect(() => {
    if (isOpen) {
      if (planoExistente) {
        form.reset({
          ...planoExistente,
          prazo_inicio: planoExistente.prazo_inicio 
            ? formatDateTimeLocalBrazil(planoExistente.prazo_inicio)
            : undefined,
          prazo_conclusao: planoExistente.prazo_conclusao 
            ? formatDateTimeLocalBrazil(planoExistente.prazo_conclusao)
            : undefined,
        });
      } else {
        form.reset({
          titulo: tituloSugestao || '',
          justificativa: justificativaSugestao || '',
          status: 'aberto',
          prioridade: 'media',
        });
      }
    }
  }, [isOpen, planoExistente, tituloSugestao, justificativaSugestao, form]);
  
  const onSubmit = (data: Partial<PlanoAcao>) => {
    const cleanedData: any = {
      titulo: data.titulo,
      justificativa: data.justificativa || null,
      categoria_id: data.categoria_id || null,
      responsavel_id: data.responsavel_id || null,
      local: data.local || null,
      prazo_inicio: data.prazo_inicio ? dateTimeLocalToISO(data.prazo_inicio) : null,
      prazo_conclusao: data.prazo_conclusao ? dateTimeLocalToISO(data.prazo_conclusao) : null,
      como_fazer: data.como_fazer || null,
      custo_estimado: data.custo_estimado ? Number(data.custo_estimado) : null,
      status: data.status,
      prioridade: data.prioridade,
      observacoes: data.observacoes || null,
    };
    
    if (planoExistente) {
      updatePlanoAcao({ 
        id: planoExistente.id, 
        updates: {
          ...cleanedData,
          resposta_id: planoExistente.resposta_id,
          campo_id: planoExistente.campo_id,
        }
      });
    } else {
      createPlanoAcao({
        ...cleanedData,
        formulario_campo_resposta_id: campoRespostaId,
        resposta_id: respostaId,
        campo_id: campoId,
      });
    }
    onSuccess?.();
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-orange-600">
            {planoExistente ? 'Editar Plano de Ação' : 'Criar Plano de Ação'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* What - O que será feito? */}
          <div>
            <Label htmlFor="titulo" className="text-base font-semibold">
              O que será feito? *
            </Label>
            <Input
              id="titulo"
              {...form.register('titulo', { required: true })}
              placeholder="Descreva a ação a ser realizada"
            />
          </div>
          
          {/* Why - Por que será feito? */}
          <div>
            <Label htmlFor="justificativa" className="text-base font-semibold">
              Por que será feito?
            </Label>
            <Textarea
              id="justificativa"
              {...form.register('justificativa')}
              placeholder="Justificativa ou causa raiz"
              rows={3}
            />
          </div>
          
          {/* Categoria */}
          <div>
            <Label htmlFor="categoria" className="text-base font-semibold">
              Categoria
            </Label>
            <Select 
              defaultValue={planoExistente?.categoria_id}
              onValueChange={(value) => {
                form.setValue('categoria_id', value);
                form.setValue('responsavel_id', undefined);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent>
                {categoriasPlanoAcao.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Responsável */}
          <div>
            <Label htmlFor="responsavel" className="text-base font-semibold">
              Responsável
            </Label>
            <Select
              value={form.watch('responsavel_id') || ''}
              onValueChange={(value) => form.setValue('responsavel_id', value)}
              disabled={!categoriaIdAtual || loadingUsuarios}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  !categoriaIdAtual 
                    ? "Selecione uma categoria primeiro" 
                    : loadingUsuarios 
                      ? "Carregando..." 
                      : "Selecione o responsável"
                } />
              </SelectTrigger>
              <SelectContent>
                {usuariosGrupo.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Where - Onde será feito? */}
          <div>
            <Label htmlFor="local" className="text-base font-semibold">
             Onde será executado?
            </Label>
            <Input
              id="local"
              {...form.register('local')}
              placeholder="Local ou área"
            />
          </div>
          
          {/* When - Quando será feito? */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="prazo_inicio" className="text-base font-semibold">
                Data de Início
              </Label>
              <Input
                id="prazo_inicio"
                type="datetime-local"
                {...form.register('prazo_inicio')}
              />
            </div>
            <div>
              <Label htmlFor="prazo_conclusao" className="text-base font-semibold">
                Prazo de Conclusão *
              </Label>
              <Input
                id="prazo_conclusao"
                type="datetime-local"
                {...form.register('prazo_conclusao', { required: true })}
              />
            </div>
          </div>
          
          {/* How - Como será feito? */}
          <div>
            <Label htmlFor="como_fazer" className="text-base font-semibold">
              Como será executado?
            </Label>
            <Textarea
              id="como_fazer"
              {...form.register('como_fazer')}
              placeholder="Descreva os passos ou método"
              rows={4}
            />
          </div>
          
          {/* How Much - Quanto custará? */}
          <div>
            <Label htmlFor="custo_estimado" className="text-base font-semibold">
              Custo Estimado (R$)
            </Label>
            <Input
              id="custo_estimado"
              type="number"
              step="0.01"
              {...form.register('custo_estimado')}
              placeholder="0.00"
            />
          </div>
          
          {/* Prioridade */}
          <div>
            <Label htmlFor="prioridade" className="text-base font-semibold">
              Prioridade
            </Label>
            <Select 
              value={form.watch('prioridade')}
              onValueChange={(value) => form.setValue('prioridade', value as any)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="baixa">Baixa</SelectItem>
                <SelectItem value="media">Média</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="critica">Crítica</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          {planoExistente && (
            <div>
              <Label htmlFor="status" className="text-base font-semibold">
                Status
              </Label>
              <Select 
                value={form.watch('status')}
                onValueChange={(value) => form.setValue('status', value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Observações */}
          <div>
            <Label htmlFor="observacoes">Observações Adicionais</Label>
            <Textarea
              id="observacoes"
              {...form.register('observacoes')}
              rows={3}
            />
          </div>
          
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating}>
              {(isCreating || isUpdating) ? 'Salvando...' : 
               planoExistente ? 'Atualizar Plano de Ação' : 'Criar Plano de Ação'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
